// Libs
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  DocumentArrowDownIcon,
  PencilIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";

// Local
import * as strings from "../../strings";
import {
  ExistingPurchaseOrder,
  getReadablePOStatus,
  OPOStatus,
} from "../../models/purchase-order";
import { ExistingVendor } from "../../models/vendor";
import { ExistingCraftRecord } from "../../models/craft-record";
import { ExistingPurchaseOrderReceipt } from "../../models/purchase-order-receipt";
import HeadingOne from "../../components/HeadingOne";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import { DollarCurrency } from "../../currency";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import ReceiptsSection from "../../components/purchaseOrders/ReceiptsSection";

interface Props {
  /* DATA */
  purchaseOrder: ExistingPurchaseOrder;
  siteKey: string;
  vendor: ExistingVendor | null;
  job: ExistingCraftRecord | null;
  receipts: ExistingPurchaseOrderReceipt[];
  /* FUNCTIONS */
  navigateBack?: () => void; // Optional custom navigation function
  navigateToJob?: () => void; // Optional navigation to job page
  handleLinkToJob?: () => void; // Optional function to link to a job
  handleUnlinkJob?: () => void; // Optional function to unlink from a job
  unlinkingJob?: boolean; // Loading state for unlinking
  handleReceiveItems: () => void;
  handleReceiveAll: () => void;
  handleEmailPurchaseOrder: () => void;
  handleGeneratePDF: () => void;
  generatingPDF?: boolean; // Loading state for PDF generation
  handleEditVendorInfo: () => void;
  handleEditDeliveryInfo: () => void;
  handleEditItems: () => void;
  handleEditNotes: () => void;
  handleEditDatesAndTerms: () => void;
  // Receipt handlers
  onCreateReceipt: () => void;
  onEditReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  onViewReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  onDeleteReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  receiptLoading?: boolean;
  // Status management handlers
  handleChangeToPending?: () => void;
  handleCancelPurchaseOrder?: () => void;
  statusUpdateLoading?: boolean;
}

export default function ViewPurchaseOrderPage(props: Props) {
  const navigate = useNavigate();
  const [siteKeyLocationList] = useSiteKeyLocationsStore((state) => [
    state.siteKeyLocationList,
    state.getLocationTitle,
  ]);

  const location = siteKeyLocationList.find(
    (location) => location.id === props.purchaseOrder.locationID,
  );

  // Handle navigation back to previous page
  const handleNavigateBack = () => {
    if (props.navigateBack) {
      props.navigateBack();
    } else {
      navigate(-1); // Default to browser history back
    }
  };

  // Calculate total values
  const subtotal = props.purchaseOrder.subTotal;
  const taxAmount = props.purchaseOrder.totalTaxAmount;
  const totalAmount = props.purchaseOrder.totalAmount;

  // Formatting helpers
  const formatMoney = (amount: number) => {
    return DollarCurrency.format(amount);
  };

  // Helper function to get color class for quantity received
  const getQuantityReceivedColorClass = (
    received: number,
    ordered: number,
  ): string => {
    if (received === ordered) {
      return "text-green-600 bg-green-50"; // Green for complete
    } else if (received > ordered) {
      return "text-red-600 bg-red-50"; // Red for over-received
    } else {
      return "text-yellow-600 bg-yellow-50"; // Yellow for under-received
    }
  };

  // Helper function to check if anything has been received
  const hasReceivedItems = () => {
    return (
      props.receipts.length > 0 &&
      props.receipts.some((receipt) => receipt.totalQuantityReceived > 0)
    );
  };

  // Helper function to determine if status change buttons should be shown
  const canChangeToPending = props.purchaseOrder.status === OPOStatus.DRAFT;
  const canCancel =
    (props.purchaseOrder.status === OPOStatus.DRAFT ||
      props.purchaseOrder.status === OPOStatus.PENDING) &&
    !hasReceivedItems();

  // Helper function to determine if receiving items is allowed
  const canReceiveItems = props.purchaseOrder.status !== OPOStatus.CANCELED;

  return (
    <div className="mx-auto w-full px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <button
            type="button"
            onClick={handleNavigateBack}
            className="mr-4 rounded-full p-2 hover:bg-gray-100"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
          <div>
            <HeadingOne fontSize="text-2xl">
              {strings.VIEW_PURCHASE_ORDER}
            </HeadingOne>
          </div>
        </div>
        <div className="text-right">
          <div className="flex flex-col items-end gap-2">
            <div className="rounded-md border-2 border-gray-200 bg-gray-50 px-4 py-2 text-xl font-semibold text-gray-700">
              PO#: {props.purchaseOrder.poNumber}
            </div>
            <div className="rounded-md bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
              {getReadablePOStatus(props.purchaseOrder.status)}
            </div>
            <div className="text-lg font-medium text-gray-900">
              Total Amount: {formatMoney(props.purchaseOrder.totalAmount)}
            </div>
          </div>
        </div>
      </div>

      {/* Job Navigation Section - Show if purchase order is linked to a job */}
      {props.purchaseOrder.craftRecordID &&
        props.navigateToJob &&
        props.job && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BriefcaseIcon className="mr-2 h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-md font-medium text-blue-800">
                    This purchase order is linked to a job
                  </p>
                  <p className="text-sm text-blue-600">{props.job.title}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <BaseButtonSecondary
                  onClick={props.navigateToJob}
                  className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <BriefcaseIcon className="h-4 w-4" />
                  View Job
                </BaseButtonSecondary>
                {props.handleUnlinkJob && (
                  <BaseButtonSecondary
                    onClick={props.handleUnlinkJob}
                    disabled={props.unlinkingJob}
                    className="flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {props.unlinkingJob ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-red-700"></div>
                        Unlinking...
                      </>
                    ) : (
                      <>
                        <BriefcaseIcon className="h-4 w-4" />
                        Unlink
                      </>
                    )}
                  </BaseButtonSecondary>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Not Linked to Job Section - Show if purchase order is not linked */}
      {!props.purchaseOrder.craftRecordID && props.handleLinkToJob && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BriefcaseIcon className="mr-2 h-5 w-5 text-amber-600" />
              <div>
                <p className="text-md font-medium text-amber-800">
                  This purchase order is not linked to a job
                </p>
                <p className="text-sm text-amber-600">
                  Link it to a job if this purchase order is specifically for a
                  job
                </p>
              </div>
            </div>
            <BaseButtonSecondary
              onClick={props.handleLinkToJob}
              className="flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
            >
              <BriefcaseIcon className="h-4 w-4" />
              Link to Job
            </BaseButtonSecondary>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="mb-6 flex flex-wrap justify-end gap-3">
        {/* Status Management Buttons */}
        {canChangeToPending && props.handleChangeToPending && (
          <BaseButtonSecondary
            onClick={props.handleChangeToPending}
            disabled={props.statusUpdateLoading}
            className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {props.statusUpdateLoading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-green-700"></div>
                Updating...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5" />
                Mark as Pending
              </>
            )}
          </BaseButtonSecondary>
        )}

        {canCancel && props.handleCancelPurchaseOrder && (
          <BaseButtonSecondary
            onClick={props.handleCancelPurchaseOrder}
            disabled={props.statusUpdateLoading}
            className="flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {props.statusUpdateLoading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-red-700"></div>
                Canceling...
              </>
            ) : (
              <>
                <XCircleIcon className="h-5 w-5" />
                Cancel Purchase Order
              </>
            )}
          </BaseButtonSecondary>
        )}

        <BaseButtonSecondary
          onClick={props.handleEmailPurchaseOrder}
          className="flex items-center gap-2"
        >
          <EnvelopeIcon className="h-5 w-5" />
          Email
        </BaseButtonSecondary>

        <BaseButtonSecondary
          onClick={props.handleGeneratePDF}
          disabled={props.generatingPDF}
          className="flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {props.generatingPDF ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-600"></div>
              Generating...
            </>
          ) : (
            <>
              <DocumentArrowDownIcon className="h-5 w-5" />
              Generate PDF
            </>
          )}
        </BaseButtonSecondary>

        {canReceiveItems && (
          <BaseButtonPrimary
            onClick={props.handleReceiveItems}
            className="flex items-center gap-2"
          >
            Receive Items
          </BaseButtonPrimary>
        )}

        {!canReceiveItems && (
          <div className="flex items-center gap-2 text-red-600">
            <span className="text-sm">
              Cannot receive items for canceled purchase orders
            </span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="rounded-lg bg-white shadow-md">
        <div className="space-y-8 p-8">
          {/* Purchase Order Information Section */}
          <div className="border-b pb-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Purchase Order Information
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Left column - Vendor info */}
              <div>
                <h3 className="mb-2 font-medium text-gray-700">Vendor</h3>
                <div className="rounded-md bg-gray-50 p-4">
                  {props.vendor ? (
                    <div className="space-y-3">
                      {/* Vendor name and logo */}
                      <div className="flex items-center space-x-3">
                        {props.vendor.logoURL ? (
                          <img
                            src={props.vendor.logoURL}
                            alt={`${props.vendor.name} logo`}
                            className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-300 bg-gray-200">
                            <span className="text-sm font-medium text-gray-500">
                              {props.vendor.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-lg font-semibold">
                            {props.vendor.name}
                          </p>
                          {props.vendor.website && (
                            <a
                              href={props.vendor.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline"
                            >
                              {props.vendor.website}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Contact information */}
                      <div className="space-y-1">
                        {props.vendor.email &&
                          props.vendor.email.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm">
                                {props.vendor.email.join(", ")}
                              </p>
                            </div>
                          )}
                        {props.vendor.phone &&
                          props.vendor.phone.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="text-sm">
                                {props.vendor.phone.join(", ")}
                              </p>
                            </div>
                          )}
                      </div>

                      {/* Address */}
                      <div>
                        <p className="text-xs text-gray-500">Address</p>
                        <div className="text-sm">
                          <p>{props.vendor.addressLine1}</p>
                          {props.vendor.addressLine2 && (
                            <p>{props.vendor.addressLine2}</p>
                          )}
                          <p>
                            {props.vendor.city}, {props.vendor.state}{" "}
                            {props.vendor.zipCode}
                          </p>
                          <p>{props.vendor.country}</p>
                        </div>
                      </div>

                      {/* Payment terms */}
                      {props.vendor.paymentTerms && (
                        <div>
                          <p className="text-xs text-gray-500">
                            Default Payment Terms
                          </p>
                          <p className="text-sm">{props.vendor.paymentTerms}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500">Unknown Vendor</p>
                  )}
                </div>
              </div>

              {/* Middle column - Dates and Terms */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Dates & Terms</h3>
                  <button
                    onClick={props.handleEditDatesAndTerms}
                    className="hover:text-primary-dark flex items-center gap-1 text-primary"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                </div>

                <div className="space-y-3 rounded-md bg-gray-50 p-4">
                  <div>
                    <p className="text-sm text-gray-500">Issue Date</p>
                    <p>
                      {convertToReadableTimestamp(
                        props.purchaseOrder.issueDate,
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Terms</p>
                    <p>{props.purchaseOrder.paymentTerms || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vendor Reference #</p>
                    <p>{props.purchaseOrder.vendorReferenceNumber || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Right column - Delivery Address */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">
                    Delivery/Shipping
                  </h3>
                  <button
                    onClick={props.handleEditDeliveryInfo}
                    className="hover:text-primary-dark flex items-center gap-1 text-primary"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                </div>

                <div className="rounded-md bg-gray-50 p-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500">Delivery Location</p>
                    <p className="text-sm font-medium text-gray-700">
                      {props.purchaseOrder.deliveryLocation}
                    </p>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500">
                      Expected Delivery Date
                    </p>
                    <p className="text-sm text-gray-700">
                      {props.purchaseOrder.expectedDeliveryDate
                        ? convertToReadableTimestamp(
                            props.purchaseOrder.expectedDeliveryDate,
                          )
                        : "Not specified"}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="mb-1 text-xs text-gray-500">
                      Delivery Address
                    </p>
                    {location?.title && (
                      <p className="text-sm font-semibold">{location.title}</p>
                    )}
                    <p className="text-sm">
                      {props.purchaseOrder.deliveryAddressLine1}
                    </p>
                    {props.purchaseOrder.deliveryAddressLine2 && (
                      <p className="text-sm">
                        {props.purchaseOrder.deliveryAddressLine2}
                      </p>
                    )}
                    <p className="text-sm">
                      {props.purchaseOrder.deliveryCity},{" "}
                      {props.purchaseOrder.deliveryState}{" "}
                      {props.purchaseOrder.deliveryZipCode}
                    </p>
                    <p className="text-sm">
                      {props.purchaseOrder.deliveryCountry}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Notes</h2>
              <button
                onClick={props.handleEditNotes}
                className="hover:text-primary-dark flex items-center gap-1 text-primary"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Edit</span>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 font-medium text-gray-700">
                  Notes for Vendor
                </h3>
                <div className="min-h-[100px] rounded-md bg-gray-50 p-4">
                  {props.purchaseOrder.notes ? (
                    <p className="whitespace-pre-wrap">
                      {props.purchaseOrder.notes}
                    </p>
                  ) : (
                    <p className="italic text-gray-400">No notes for vendor</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="mb-2 font-medium text-gray-700">
                  Internal Notes
                </h3>
                <div className="min-h-[100px] rounded-md bg-gray-50 p-4">
                  {props.purchaseOrder.internalNotes ? (
                    <p className="whitespace-pre-wrap">
                      {props.purchaseOrder.internalNotes}
                    </p>
                  ) : (
                    <p className="italic text-gray-400">No internal notes</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div className="border-b pb-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Line Items
              </h2>
              <button
                onClick={props.handleEditItems}
                className="hover:text-primary-dark flex items-center gap-1 text-primary"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Edit</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Item
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Quantity
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Received
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Unit Price
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {props.purchaseOrder.lineItems.map((item) => (
                    <tr key={item.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {item.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.description || ""}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {item.quantity}
                      </td>
                      <td
                        className={`whitespace-nowrap rounded-md px-6 py-4 text-sm font-medium ${getQuantityReceivedColorClass(item.quantityReceived, item.quantity)}`}
                      >
                        {item.quantityReceived}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatMoney(item.unitPrice)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900">
                        {formatMoney(item.subTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Order Totals */}
            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-xs">
                <div className="rounded-md bg-gray-50 p-4">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{formatMoney(subtotal)}</span>
                  </div>

                  <div className="flex justify-between border-b border-gray-200 py-2">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">
                      {formatMoney(taxAmount)}
                    </span>
                  </div>

                  <div className="flex justify-between py-2 font-semibold">
                    <span>Total:</span>
                    <span>{formatMoney(totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Receipts Section */}
          <ReceiptsSection
            purchaseOrder={props.purchaseOrder}
            receipts={props.receipts}
            onCreateReceipt={props.onCreateReceipt}
            onReceiveAll={props.handleReceiveAll}
            onEditReceipt={props.onEditReceipt}
            onViewReceipt={props.onViewReceipt}
            onDeleteReceipt={props.onDeleteReceipt}
            receiptLoading={props.receiptLoading}
          />
        </div>
      </div>
    </div>
  );
}
